import { useState, useEffect } from 'react';
import { dispararNotificacaoChamado } from '@/hooks/useTicketNotifications';
import { useBottomSheet } from '@/hooks/useBottomSheet';

interface TicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  participantePre?: Participante | null;
  defaultCategory?: string;
  defaultSubject?: string;
  defaultDescription?: string;
  defaultPriority?: string;
}

interface Participante {
  id: string;
  nome: string;
  email: string;
  cpf?: string;
  cnpj?: string;
  telefone?: string;
  departamento: string;
  cargo: string;
  publicoAlvo: 'PF' | 'PJ' | 'MEI' | 'CLT' | 'AUTONOMO' | 'REPRESENTANTE' | 'DISTRIBUIDOR';
  endereco?: string;
  cidade?: string;
  estado?: string;
  dataNascimento?: string;
  dataAdmissao?: string;
}

interface DadosAlterados {
  [key: string]: string;
}

export function TicketModal({ isOpen, onClose, participantePre, defaultCategory = '', defaultSubject = '', defaultDescription = '', defaultPriority = 'media' }: TicketModalProps) {
  const [formData, setFormData] = useState({
    category: defaultCategory,
    priority: defaultPriority,
    subject: defaultSubject,
    description: defaultDescription,
    attachment: null as File | null,
    participanteId: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  const [searchParticipante, setSearchParticipante] = useState('');
  const [showParticipantesList, setShowParticipantesList] = useState(false);
  const [dadosEditados, setDadosEditados] = useState<DadosAlterados>({});

  // Efeito para pré-selecionar participante quando fornecido
  useEffect(() => {
    if (participantePre) {
      setFormData(prev => ({ 
        ...prev, 
        category: 'participante',
        participanteId: participantePre.id 
      }));
      setSearchParticipante(participantePre.nome);
      setShowParticipantesList(false);
    }
  }, [participantePre]);

  // Mock de participantes - em produção, isso viria de uma API
  const mockParticipantes: Participante[] = [
    {
      id: '1',
      nome: 'Ana Silva',
      email: 'ana.silva@empresa.com',
      cpf: '123.456.789-01',
      telefone: '(11) 99999-8888',
      departamento: 'Vendas',
      cargo: 'Consultora de Vendas',
      publicoAlvo: 'PF',
      endereco: 'Rua das Flores, 123',
      cidade: 'São Paulo',
      estado: 'SP',
      dataNascimento: '15/03/1990',
      dataAdmissao: '15/01/2024'
    },
    {
      id: '2',
      nome: 'Carlos Eduardo Lima',
      email: 'carlos.lima@empresa.com',
      cpf: '987.654.321-02',
      telefone: '(11) 98888-7777',
      departamento: 'Marketing',
      cargo: 'Analista de Marketing',
      publicoAlvo: 'CLT',
      endereco: 'Av. Paulista, 1000',
      cidade: 'São Paulo',
      estado: 'SP',
      dataNascimento: '22/07/1988',
      dataAdmissao: '10/01/2024'
    },
    {
      id: '3',
      nome: 'Mariana Santos',
      email: 'mariana.santos@empresa.com',
      cpf: '456.789.123-03',
      telefone: '(11) 97777-6666',
      departamento: 'RH',
      cargo: 'Coordenadora de RH',
      publicoAlvo: 'CLT',
      endereco: 'Rua Augusta, 500',
      cidade: 'São Paulo',
      estado: 'SP',
      dataNascimento: '10/11/1985',
      dataAdmissao: '05/01/2024'
    },
    {
      id: '7',
      nome: 'Roberto Costa',
      email: 'roberto.costa@empresa.com',
      cnpj: '12.345.678/0001-90',
      telefone: '(11) 96666-5555',
      departamento: 'Vendas',
      cargo: 'Gerente de Vendas',
      publicoAlvo: 'PJ',
      endereco: 'Av. Faria Lima, 2000',
      cidade: 'São Paulo',
      estado: 'SP',
      dataAdmissao: '01/01/2024'
    },
    {
      id: '5',
      nome: 'Juliana Rodrigues',
      email: 'juliana.rodrigues@empresa.com',
      cpf: '321.654.987-05',
      telefone: '(11) 95555-4444',
      departamento: 'Financeiro',
      cargo: 'Analista Financeiro',
      publicoAlvo: 'MEI',
      endereco: 'Rua Oscar Freire, 300',
      cidade: 'São Paulo',
      estado: 'SP',
      dataNascimento: '05/09/1992',
      dataAdmissao: '08/01/2024'
    }
  ];

  const filteredParticipantes = mockParticipantes.filter(p => 
    p.nome.toLowerCase().includes(searchParticipante.toLowerCase()) ||
    p.email.toLowerCase().includes(searchParticipante.toLowerCase()) ||
    (p.cpf && p.cpf.includes(searchParticipante)) ||
    (p.cnpj && p.cnpj.includes(searchParticipante))
  );

  const participanteSelecionado = mockParticipantes.find(p => p.id === formData.participanteId);

  const camposEditaveis = [
    { id: 'email', label: 'Email', tipo: 'email' },
    { id: 'telefone', label: 'Telefone', tipo: 'tel' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newTicketNumber = `TK${Date.now().toString().slice(-6)}`;
    setTicketNumber(newTicketNumber);
    setSubmitted(true);

    // Dispara notificação no sino do participante
    dispararNotificacaoChamado({
      ticketNumber: newTicketNumber,
      subject: formData.subject,
      novoStatus: 'aberto',
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, attachment: e.target.files[0] });
    }
  };

  const handleSelecionarParticipante = (participante: Participante) => {
    setFormData({ ...formData, participanteId: participante.id });
    setSearchParticipante(participante.nome);
    setShowParticipantesList(false);
    setDadosEditados({});
  };

  const handleEditarCampo = (campoId: string, novoValor: string) => {
    setDadosEditados({
      ...dadosEditados,
      [campoId]: novoValor
    });
  };

  const getValorOriginal = (campo: string): string => {
    if (!participanteSelecionado) return '';
    return (participanteSelecionado as any)[campo] || '';
  };

  const getValorAtual = (campo: string): string => {
    return dadosEditados[campo] !== undefined ? dadosEditados[campo] : getValorOriginal(campo);
  };

  const foiAlterado = (campo: string): boolean => {
    return dadosEditados[campo] !== undefined && dadosEditados[campo] !== getValorOriginal(campo);
  };

  const getPublicoAlvoColor = (publico: string) => {
    const colors = {
      'PF': 'bg-blue-100 text-blue-800',
      'PJ': 'bg-purple-100 text-purple-800',
      'MEI': 'bg-green-100 text-green-800',
      'CLT': 'bg-orange-100 text-orange-800',
      'AUTONOMO': 'bg-teal-100 text-teal-800',
      'REPRESENTANTE': 'bg-indigo-100 text-indigo-800',
      'DISTRIBUIDOR': 'bg-pink-100 text-pink-800'
    };
    return colors[publico as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getTipoIcon = (publico: string) => {
    const icons = {
      'PF': 'ri-user-line',
      'PJ': 'ri-building-line',
      'MEI': 'ri-store-line',
      'CLT': 'ri-briefcase-line',
      'AUTONOMO': 'ri-user-star-line',
      'REPRESENTANTE': 'ri-user-voice-line',
      'DISTRIBUIDOR': 'ri-truck-line'
    };
    return icons[publico as keyof typeof icons] || 'ri-user-line';
  };

  const camposAlterados = Object.keys(dadosEditados).filter(campo => foiAlterado(campo));

  const { mounted, sheetClass, backdropClass, handleClose: animatedClose } = useBottomSheet({ isOpen, onClose });

  if (!mounted) return null;

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-checkbox-circle-line text-emerald-600 text-4xl"></i>
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Chamado Criado!</h3>
          <p className="text-slate-600 mb-4">
            Seu chamado foi registrado com sucesso.
          </p>

          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
            <div className="text-xs text-slate-500 mb-1 font-medium uppercase tracking-wide">Número do Chamado</div>
            <div className="text-2xl font-bold text-orange-600 font-mono">{ticketNumber}</div>
          </div>

          {/* Dica sobre notificação no sino */}
          <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-6 text-left">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-rose-100 rounded-lg">
              <i className="ri-notification-3-line text-rose-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700">Fique de olho no sino</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Você receberá uma notificação aqui mesmo sempre que o status do seu chamado for atualizado pela equipe de suporte.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all cursor-pointer font-semibold whitespace-nowrap"
          >
            Entendido
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4 ${backdropClass}`} onClick={animatedClose}>
      <div className={`bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-4xl w-full flex flex-col ${sheetClass}`} style={{ maxHeight: 'calc(100dvh - 64px)' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-ticket-line text-blue-600 text-xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Abrir Chamado</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6 pb-20 sm:pb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Categoria *
                </label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => {
                    setFormData({ 
                      ...formData, 
                      category: e.target.value,
                      participanteId: ''
                    });
                    setSearchParticipante('');
                    setDadosEditados({});
                  }}
                  className="w-full px-4 py-2.5 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-sm bg-white text-gray-900 appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="">Selecione uma categoria</option>
                  <option value="participante">Participante</option>
                  <option value="financeiro">Financeiro</option>
                  <option value="cadastro">Cadastro</option>
                  <option value="gamificacao">Gamificação</option>
                  <option value="relatorio">Relatórios</option>
                  <option value="outro">Outro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Prioridade *
                </label>
                <select
                  required
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-4 py-2.5 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-sm bg-white text-gray-900 appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="baixa">Baixa</option>
                  <option value="media">Média</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
            </div>

            {/* Seleção de Participante - Apenas para Participante */}
            {formData.category === 'participante' && (
              <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                <div className="flex items-center space-x-2 mb-3">
                  <i className="ri-user-search-line text-blue-600 text-lg"></i>
                  <h4 className="text-sm font-semibold text-gray-900">Participante</h4>
                </div>

                <div className="relative mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar Participante
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <i className="ri-search-line text-gray-400"></i>
                    </div>
                    <input
                      type="text"
                      value={searchParticipante}
                      onChange={(e) => {
                        setSearchParticipante(e.target.value);
                        setShowParticipantesList(true);
                        // Limpa o participante selecionado se o usuário começar a digitar novamente
                        if (formData.participanteId) {
                          setFormData({ ...formData, participanteId: '' });
                          setDadosEditados({});
                        }
                      }}
                      onFocus={() => {
                        // Só mostra a lista se não houver participante selecionado
                        if (!formData.participanteId) {
                          setShowParticipantesList(true);
                        }
                      }}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                      placeholder="Digite nome, email, CPF ou CNPJ..."
                    />
                    {formData.participanteId && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, participanteId: '' });
                          setSearchParticipante('');
                          setDadosEditados({});
                          setShowParticipantesList(false);
                        }}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                        title="Limpar seleção"
                      >
                        <i className="ri-close-circle-fill text-lg"></i>
                      </button>
                    )}
                  </div>

                  {/* Lista de Participantes */}
                  {showParticipantesList && searchParticipante && !formData.participanteId && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredParticipantes.length > 0 ? (
                        filteredParticipantes.map((participante) => (
                          <div
                            key={participante.id}
                            onClick={() => handleSelecionarParticipante(participante)}
                            className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-gray-900">{participante.nome}</div>
                                <div className="text-xs text-gray-600 mt-1">
                                  {participante.email} • {participante.departamento}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {participante.cpf && `CPF: ${participante.cpf}`}
                                  {participante.cnpj && `CNPJ: ${participante.cnpj}`}
                                </div>
                              </div>
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ml-2 ${getPublicoAlvoColor(participante.publicoAlvo)}`}>
                                <i className={`${getTipoIcon(participante.publicoAlvo)} mr-1`}></i>
                                {participante.publicoAlvo}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-gray-500">
                          Nenhum participante encontrado
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Formulário de Edição do Participante Selecionado */}
                {formData.participanteId && participanteSelecionado && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <i className="ri-user-line text-blue-600"></i>
                        <h5 className="text-sm font-semibold text-gray-900">Dados Cadastrais</h5>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPublicoAlvoColor(participanteSelecionado.publicoAlvo)}`}>
                          <i className={`${getTipoIcon(participanteSelecionado.publicoAlvo)} mr-1`}></i>
                          {participanteSelecionado.publicoAlvo}
                        </span>
                      </div>
                    </div>

                    {/* Informações do Participante */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-gray-500">Nome:</span>
                          <div className="font-semibold text-gray-900 mt-1">{participanteSelecionado.nome}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Departamento:</span>
                          <div className="font-semibold text-gray-900 mt-1">{participanteSelecionado.departamento}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Cargo:</span>
                          <div className="font-semibold text-gray-900 mt-1">{participanteSelecionado.cargo}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">{participanteSelecionado.cpf ? 'CPF' : 'CNPJ'}:</span>
                          <div className="font-semibold text-gray-900 mt-1">
                            {participanteSelecionado.cpf || participanteSelecionado.cnpj}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Email:</span>
                          <div className="font-semibold text-gray-900 mt-1">{participanteSelecionado.email}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Telefone:</span>
                          <div className="font-semibold text-gray-900 mt-1">{participanteSelecionado.telefone || '-'}</div>
                        </div>
                      </div>
                    </div>

                    {camposAlterados.length > 0 && (
                      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-amber-800">
                          <i className="ri-alert-line"></i>
                          <span className="text-sm font-semibold">
                            {camposAlterados.length} campo(s) alterado(s)
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <div className="text-xs font-semibold text-gray-700 mb-3">Campos Editáveis:</div>
                      <div className="grid grid-cols-2 gap-4">
                        {camposEditaveis.map((campo) => {
                          const alterado = foiAlterado(campo.id);
                          const valorOriginal = getValorOriginal(campo.id);
                          const valorAtual = getValorAtual(campo.id);

                          return (
                            <div key={campo.id}>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                {campo.label}
                              </label>
                              <input
                                type={campo.tipo}
                                value={valorAtual}
                                onChange={(e) => handleEditarCampo(campo.id, e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                                  alterado
                                    ? 'border-amber-400 bg-amber-50'
                                    : 'border-gray-300 bg-white'
                                }`}
                              />
                              {alterado && (
                                <div className="mt-1 flex items-start space-x-2 text-xs">
                                  <div className="flex-1">
                                    <div className="text-gray-500">
                                      <span className="font-medium">Anterior:</span> {valorOriginal}
                                    </div>
                                    <div className="text-amber-700 font-medium">
                                      <span className="font-medium">Novo:</span> {valorAtual}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const novoDados = { ...dadosEditados };
                                      delete novoDados[campo.id];
                                      setDadosEditados(novoDados);
                                    }}
                                    className="text-red-600 hover:text-red-800 cursor-pointer"
                                    title="Desfazer alteração"
                                  >
                                    <i className="ri-close-circle-line"></i>
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Assunto *
              </label>
              <input
                type="text"
                required
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Descreva brevemente o problema"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 placeholder-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Descrição *
              </label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva detalhadamente o problema ou solicitação..."
                rows={6}
                maxLength={500}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm text-gray-900 placeholder-gray-500"
              ></textarea>
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.description.length}/500 caracteres
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Anexo (opcional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  id="file-upload"
                  onChange={handleFileChange}
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <i className="ri-upload-cloud-line text-gray-400 text-2xl"></i>
                  </div>
                  {formData.attachment ? (
                    <div className="text-sm text-gray-700 font-medium">
                      {formData.attachment.name}
                    </div>
                  ) : (
                    <>
                      <div className="text-sm text-gray-700 font-medium mb-1">
                        Clique para fazer upload
                      </div>
                      <div className="text-xs text-gray-500">
                        PNG, JPG, PDF até 10MB
                      </div>
                    </>
                  )}
                </label>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer font-semibold whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer font-semibold whitespace-nowrap"
              >
                Abrir Chamado
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
